import { createQuote as upstreamCreateQuote } from 'customer-flows/gql/mutations/create-quote'
import { updateQuote as upstreamUpdateQuote } from 'customer-flows/gql/mutations/update-quote'
import { applyRedemptionCode as upstreamApplyRedemptionCode } from 'customer-flows/gql/mutations/apply-redemption-code'
import { removeRedemptionCode as upstreamRemoveRedemptionCode } from 'customer-flows/gql/mutations/remove-redemption-code'
import { applyTipToQuote as upstreamApplyTipToQuote } from 'customer-flows/gql/mutations/apply-tip-to-quote'
import { getRefid } from 'customer-flows/services/quote-builder'
import Logger from 'customer-flows/utils/logger'
import uid from 'customer-flows/utils/uid'

type ApplyRedemptionCodeArgs = Omit<ApplyRedemptionCodeInput, 'quoteId'>
type RemoveRedemptionCodeArgs = Omit<RemoveRedemptionCodeInput, 'quoteId'>
type ApplyTipToQuoteArgs = Omit<ApplyTipToQuoteInput, 'quoteId'>

const QUOTE_RETRY_LIMIT = 5

// The main booking is the one with the refid the quote builder gave it, as in `getBookingQuoteForRef`
function getMainBookingTime(quote?: Quote | null): string | undefined {
  const refid = getRefid('bookingQuotes')
  const snapshot = quote?.bookingQuotes?.find((bookingQuote) => bookingQuote.refid === refid)?.timeSnapshot

  return snapshot ? `${snapshot.from}/${snapshot.until}` : undefined
}

export default function quote({ stores, callerContext, services, dispatch }: CommandArgs) {
  const quoteStore: QuoteStore = stores['quote']
  const bookingFlowStore: BookingFlowStore = stores['program/booking-flow']
  const eventTracker: EventTracker = services['event-tracker']
  const flowRouter: FlowRouter = services['flow-router']
  const session: Session = services['session']

  // Stores the in-flight quote promise and hands it back to the caller.
  function setQuotePromise(promise: Promise<Quote | undefined>): Promise<Quote | undefined> {
    quoteStore.quote.promise = promise

    return promise
  }

  function createOrUpdateQuote(quoteInput: QuoteStrictInput): Promise<Quote | undefined | null> {
    if (quoteStore.id) {
      return updateQuote(quoteInput)
    } else {
      return createQuote(quoteInput)
    }
  }

  function createQuote(quoteInput: QuoteStrictInput, retryCount = 0): Promise<Quote | undefined | null> {
    return setQuotePromise(
      upstreamCreateQuote(callerContext, quoteInput).then((response) => {
        const data = response?.createQuoteV2
        const quote = data?.quote
        quoteStore.cachedQuoteId = quoteStore.cachedQuoteId ?? quote?.id

        const invalidSessionError = data?.errors?.find((e: ResponseError) => e.detail === 'invalid_session_id')

        if (invalidSessionError && retryCount < QUOTE_RETRY_LIMIT) {
          Logger.warn('Quote: invalid_session_id — resetting session and retrying', { retryCount })
          session.resetId()
          return createQuote(quoteInput, retryCount + 1).then((q) => q ?? undefined)
        }

        if (data?.errors?.length) {
          Logger.warn('Quote: createQuote returned errors', { errors: data.errors })
        }
        if (data?.warnings?.length) {
          Logger.warn('Quote: createQuote returned warnings', { warnings: data.warnings })
        }
        Logger.info(`Quote: Quote created (id: ${quote?.id})`)

        return quote
      })
    )
  }

  function updateQuote(quoteInput: QuoteSparseInput): Promise<Quote | undefined | null> {
    if (!quoteStore.id) {
      return Promise.reject(new Error('Cannot update quote without an ID'))
    }

    const previousBookingTime = getMainBookingTime(quoteStore.quote.value)

    return setQuotePromise(
      upstreamUpdateQuote(callerContext, quoteInput, quoteStore.id).then((response) => {
        const data = response?.updateQuoteV2

        if (bookingFlowStore.bundleItems.length && data?.quote && getMainBookingTime(data.quote) !== previousBookingTime) {
          dispatch('bundle:invalidateAvailability')
        }

        if (data?.errors?.length) {
          Logger.warn('Quote: updateQuote returned errors', { errors: data.errors })
        }
        if (data?.warnings?.length) {
          Logger.warn('Quote: updateQuote returned warnings', { warnings: data.warnings })
        }

        return data?.quote
      })
    )
  }

  function getPromoCodeIdempotencyKey(codeString: string): string {
    let idempotencyKey = uid()

    if (quoteStore.promoCodes && codeString) {
      const storedPromoCode = (quoteStore.promoCodes || []).find(
        ({ code }: QuotePromoCode) => code?.toUpperCase() === codeString.toUpperCase()
      )
      if (storedPromoCode?.refid) {
        idempotencyKey = storedPromoCode.refid
      }
    }

    return idempotencyKey
  }

  function applyRedemptionCode(redemptionCode: string): Promise<Quote | undefined | null> {
    const input: ApplyRedemptionCodeArgs = {
      idempotencyKey: getPromoCodeIdempotencyKey(redemptionCode),
      redemptionCode
    }

    if (!quoteStore.id) {
      Logger.warn('Quote: Cannot apply redemption code without a quote ID', { redemptionCode })
      return Promise.resolve(undefined)
    }

    const vars: ApplyRedemptionCodeInput = {
      quoteId: quoteStore.id,
      ...input
    }

    return setQuotePromise(
      upstreamApplyRedemptionCode(callerContext, vars).then((response) => {
        const result = response?.applyRedemptionCode

        if (result?.__typename === 'GenericError' || result?.__typename === 'NotFoundError') {
          Logger.warn('Quote: Failed to apply redemption code', { redemptionCode, result })
          throw new Error('Failed to apply redemption code', { cause: result })
        }

        eventTracker.track('select_promotion', { quote: result?.quote })

        return result?.quote
      })
    )
  }

  function removeRedemptionCode(input: RemoveRedemptionCodeArgs): Promise<Quote | undefined | null> {
    if (!quoteStore.id) {
      return Promise.reject(new Error('Cannot remove redemption code without a quote ID'))
    }

    const vars: RemoveRedemptionCodeInput = {
      quoteId: quoteStore.id,
      ...input
    }

    return setQuotePromise(
      upstreamRemoveRedemptionCode(callerContext, vars).then((response) => {
        const result = response?.removeRedemptionCode
        if (
          result?.__typename === 'RemoveGiftCardSuccess' ||
          result?.__typename === 'RemoveStoreCreditSuccess' ||
          result?.__typename === 'RemoveExternalVoucherSuccess' ||
          result?.__typename === 'RemovePromoCodeSuccess'
        ) {
          return result.quote
        }
        throw new Error('Failed to remove redemption code')
      })
    )
  }

  async function enablePeekProtect(): Promise<void | Quote> {
    return await updateQuote({ peekProtection: true }).then(() => {
      eventTracker.track('Peek Protect Clicked')
    })
  }

  async function disablePeekProtect(): Promise<void | Quote> {
    return await updateQuote({ peekProtection: false }).then(() => {
      eventTracker.track('Peek Protect Clicked')
    })
  }

  function enableCardIssuerDiscount(): Promise<Quote | undefined | null> {
    return updateQuote({ applyCardIssuerDiscount: true })
  }

  function disableCardIssuerDiscount(): Promise<Quote | undefined | null> {
    return updateQuote({ applyCardIssuerDiscount: false })
  }

  /**
   * Applies a promo code from the query params if it exists and is not already applied
   * promoCode query param can be a promo code, a gift card or a membership redemption code.
   * All are supported by the applyRedemptionCode command.
   */
  async function applyPromoCodeFromQueryParam() {
    await quoteStore.quote.promise

    const promoCode = flowRouter.queryParams?.promoCode
    const isPromoCodeApplied = promoCode && quoteStore.isPromoCodeApplied(promoCode)

    if (!promoCode || !quoteStore.id || isPromoCodeApplied) return

    dispatch('quote:applyRedemptionCode', promoCode)
  }

  function applyTipToQuote(tipOption: WidgetTipConfigOption): Promise<Quote | undefined | null> {
    if (!tipOption.value?.amount || !tipOption.value?.currency) {
      Logger.warn('Quote: Cannot apply tipping to quote without a valid tip amount', { tipOption })
      return Promise.resolve(undefined)
    }

    const input: ApplyTipToQuoteArgs = {
      amount: {
        amount: parseFloat(tipOption.value?.amount),
        currency: tipOption.value?.currency as Currency
      }
    }

    if (!quoteStore.id) {
      Logger.warn('Quote: Cannot apply tipping to quote without a quote ID', { tipOption })
      return Promise.resolve(undefined)
    }

    const vars: ApplyTipToQuoteInput = {
      quoteId: quoteStore.id,
      ...input
    }

    return setQuotePromise(
      upstreamApplyTipToQuote(callerContext, vars).then((response) => {
        const result = response?.applyTipToQuote

        if (
          result?.__typename === 'GenericError' ||
          result?.__typename === 'NotFoundError' ||
          result?.__typename === 'InvalidDataError' ||
          result?.__typename === 'MoneyInputError'
        ) {
          Logger.warn('Quote: Failed to apply tipping', { result })
          throw new Error('Failed to apply tipping', { cause: result })
        }

        return result?.quote
      })
    )
  }

  return {
    createOrUpdateQuote,
    createQuote,
    updateQuote,
    applyRedemptionCode,
    removeRedemptionCode,
    enablePeekProtect,
    disablePeekProtect,
    enableCardIssuerDiscount,
    disableCardIssuerDiscount,
    applyPromoCodeFromQueryParam,
    applyTipToQuote
  }
}

declare global {
  export type QuoteCommands = ReturnType<typeof quote>

  // Add to global scope
  interface CommandRegistry {
    quote: QuoteCommands
  }
}
